import { storage } from "../modules";
import { closest } from "../utils";

/**
 * A component for syncing scroll position between page navigation.
 *
 * The component expects no children, and can be placed anywhere in the
 * document. Scroll position won't persist for more than a single page load.
 *
 * Scroll position is saved to session storage when a user clicks on any
 * element that has a `data-scroll-position` attribute equal to `'persist'`.
 *
 * Scroll position is restored whenever the component is mounted and a position
 * can be retrieved from storage.
 *
 * @example
 * ```html
 * <ifrs-scroll-position></ifrs-scroll-position>
 * <div>
 *   <a href="/" data-scroll-position="persist">
 *     Scroll position will persist when this link is clicked
 *   </a>
 * </div>
 * ```
 */
export default class ScrollPosition extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition.
   */
  static define(tag = "ifrs-scroll-position") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup = new Set();

  /**
   * The key for referencing the scroll position session storage entry.
   *
   * @type {string}
   */
  storageKey = "scroll-position";

  /**
   * @type {number|null}
   */
  get position() {
    const position = storage.session.get(this.storageKey);
    return Number.isInteger(position) ? position : null;
  }

  /**
   * @param {number|null} value The new scroll position, or null.
   */
  set position(value) {
    if (Number.isInteger(value)) {
      storage.session.set(this.storageKey, value);
    } else {
      storage.session.remove(this.storageKey);
    }
  }

  /**
   * Set up the component once it has been added to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected.
    if (!this.isConnected) {
      return;
    }

    // Set up the component once the document is ready.
    if (document.readyState !== "loading") {
      this.setup();
    } else {
      const setup = () => this.setup();
      document.addEventListener("DOMContentLoaded", setup);
      this.cleanup.add(() => document.removeEventListener("DOMContentLoaded", setup));
    }

    /**
     * Listen for clicks on elements with the data-scroll-position attribute,
     * and save the current scroll position when they occur.
     *
     * @param {PointerEvent} event A click event object.
     */
    const handleClick = (event) => {
      if (closest(event.target, "[data-scroll-position='persist']")) {
        this.position = window.scrollY;
      }
    };

    document.addEventListener("click", handleClick, { passive: true });
    this.cleanup.add(() => document.removeEventListener("click", handleClick));
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Restore the scroll position.
   */
  setup() {
    if (!this.dataset.setup) {
      const position = this.position;
      const hasUserScrolled = window.scrollY > 0;
      const hasBeenAWhile = window.performance.now() > 1500;

      // If a scroll position is saved in session storage, then update the
      // current scroll position and delete the storage entry.
      //
      // To ensure a decent user experience, we only restore the scroll
      // position if (1) the user has not yet scrolled the page, and (2) this
      // component has mounted relatively soon after the initial page load.
      if (Number.isInteger(position) && !hasUserScrolled && !hasBeenAWhile) {
        window.scrollTo({ top: position, behavior: "instant" });
      }

      // Unset the current position.
      this.position = null;

      // Mark the component as set up to prevent redundant action.
      this.dataset.setup = "true";
    }
  }
}
